import type { PartOfSpeech, RelationType } from "./relationTypes";

export type SegmentKind = "prefix" | "root" | "suffix" | "ending" | "infix" | "linker";

export interface MorphologySegment {
  kind: SegmentKind;
  text: string;
}

export interface MorphologyParts {
  prefix?: string | null;
  root?: string | null;
  suffix?: string | null;
  stem?: string | null;
  ending?: string | null;
  /** Ordered segments where concatenated .text reproduces the word exactly. Drives in-word color highlighting. */
  segments?: MorphologySegment[];
}

export interface DerivativeRecord {
  word: string;
  lemma?: string;
  partOfSpeech: string;
  relationType: RelationType;
  morphology?: MorphologyParts;
  meaning?: string | null;
  example?: string | null;
  register?: string | null;
  frequency?: string | null;
  confidence?: number | null;
}

export interface WordFamilyMetadata {
  rootWord: string;
  rootMeaning?: string | null;
  etymology?: string | null;
  morphology?: MorphologyParts | null;
  language: string;
  modelVersion?: string;
  sourcePhrase?: string | null;
}

export interface WordFamilyResponse {
  derivatives: DerivativeRecord[];
  metadata: WordFamilyMetadata;
  status: "ready" | "failed" | "pending";
}

export interface FetchWordFamilyOptions {
  word: string;
  language: string;
  cardId?: string | null;
  sourcePhrase?: string | null;
  force?: boolean;
  signal?: AbortSignal;
}

export class WordFamilyError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "WordFamilyError";
    this.status = status;
  }
}

const ENDPOINT = "/api/word-family";
// 90 seconds: Wiktionary (~1s) + Groq (~5s) + Gemini (~20s) + DeepSeek (~30s) + buffer.
// When upstream providers fail and the chain falls through to DeepSeek, full latency is ~50-60s.
const REQUEST_TIMEOUT_MS = 90_000;

export async function fetchWordFamily({
  word,
  language,
  cardId,
  sourcePhrase,
  force,
  signal,
}: FetchWordFamilyOptions): Promise<WordFamilyResponse> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        word,
        language,
        cardId: cardId ?? undefined,
        sourcePhrase: sourcePhrase ?? undefined,
        force: force === true,
      }),
      signal: controller.signal,
    });
    const text = await res.text();
    let data: unknown;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new WordFamilyError("Invalid JSON response", res.status);
    }
    if (!res.ok) {
      const msg = (data as { error?: string })?.error ?? `HTTP ${res.status}`;
      throw new WordFamilyError(msg, res.status);
    }
    const body = data as Partial<WordFamilyResponse>;
    if (!Array.isArray(body.derivatives)) {
      throw new WordFamilyError("Malformed response: missing derivatives", 502);
    }
    return {
      derivatives: body.derivatives,
      metadata: (body.metadata ?? { rootWord: word, language }) as WordFamilyMetadata,
      status: (body.status ?? "ready") as WordFamilyResponse["status"],
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export type { PartOfSpeech, RelationType };
